import { type InputTypeProps } from "@common/declarations";
import withValue, { type WithValueProps } from "@common/hocs/withValue";
import { useUpdated } from "@common/lib/hooks";
import { uuid } from "@common/lib/misc";
import { type VideoUrlProvider } from "@common/lib/validate";
import { usePrevious } from "@dnd-kit/utilities";
import classNames from "classnames";
import { isUndefined } from "lodash";
import React, { useMemo } from "react";
import { createEditor, type Descendant, Editor, Range, Transforms } from "slate";
import { withHistory } from "slate-history";
import { Slate, withReact } from "slate-react";
import Content, { type ElementComponentProps } from "./Content/component";
import { RichEditorContext, RichEditorContextProvider } from "./context";
import { withBreakTitle, withHelpers, withImages, withInlines, withSelectableTypes } from "./hooks";
import { fromHtml, toHtml } from "./html";
import { isHeadingNode, isMediaNode } from "./lib";
import Toolbar from "./Toolbar/component";
import HoveringToolbar from "./Toolbar/Hovering";
import { type ImageElement, type RichEditorMode, type RichEditorToolbarButtons, type SlateEditor, type VideoElement } from "./types";

import "./style.less";

type RichEditorBaseProps = InputTypeProps<string | null> & {
  allowVideoFullScreen?: boolean;
  autoFocus?: boolean;
  availableVideoProviders?: VideoUrlProvider[];
  bunnyLibraryId?: string;
  className?: string;
  colors?: string[];
  dropdownClassName?: string;
  ElementComponent?: React.ComponentType<ElementComponentProps>;
  id?: string;
  isInline?: boolean;
  isReadonly?: boolean;
  isRequired?: boolean;
  maxHeight?: number;
  minHeight?: number;
  mode?: RichEditorMode;
  onBlur?: () => void;
  onChangeMedia?: (mediaIds: string[]) => void;
  onFocus?: () => void;
  placeholder?: string;
  position?: number | "all";
  toolbar?: RichEditorToolbarButtons;
};

const RichEditorBase = (props: RichEditorBaseProps) => {
  const editor = useMemo(
    (): SlateEditor =>
      withImages(
        withBreakTitle(
          withSelectableTypes(withHelpers(withInlines(withHistory(withReact(createEditor())))), ["image", "video"])
        )
      ),
    []
  );

  const context = React.useContext(RichEditorContext);

  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const [htmlValue, setHtmlValue] = React.useState<string>();
  const [isFocused, setIsFocused] = React.useState(false);

  React.useLayoutEffect(() => {
    if (context.isCodeview) {
      setHtmlValue(toHtml(editor, props.mode));
    } else {
      if (!textAreaRef.current) return;

      editor.deleteAll();

      // Removes empty node
      Transforms.removeNodes(editor, {
        at: [0]
      });

      // Insert array of children nodes
      Transforms.insertNodes(editor, fromHtml(textAreaRef.current!.value));

      setHtmlValue(undefined);
    }
  }, [editor, context.isCodeview]);

  React.useEffect(() => {
    if (props.position === "all") {
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, [])
      });
    }
  }, [props.position]);

  React.useEffect(() => {
    props.autoFocus && Transforms.select(editor, Editor.end(editor, []));
  }, [props.autoFocus]);

  const contentStyleBase = React.useMemo(
    (): React.CSSProperties => ({
      maxHeight: props.maxHeight,
      minHeight: props.minHeight
    }),
    [props.minHeight, props.maxHeight]
  );

  const contentStyle = React.useMemo(
    (): React.CSSProperties => ({
      ...contentStyleBase,
      display: context.isCodeview ? "none" : undefined
    }),
    [contentStyleBase, context.isCodeview]
  );

  const [key, setKey] = React.useState<string>();
  const value = React.useMemo(() => fromHtml(props.value), [props.value]);
  const prevPropsValue = usePrevious(props.value);
  useUpdated(() => {
    if (prevPropsValue !== props.value && !isFocused) {
      if (!props.value) {
        Transforms.select(editor, { anchor: { path: [0, 0], offset: 0 }, focus: { path: [0, 0], offset: 0 } });
      }

      editor.children = fromHtml(props.value);
      setKey(uuid());
    }
  }, [prevPropsValue, props.value, isFocused]);

  const onValueChange = React.useCallback(
    (nodes: Descendant[]) => {
      if (props.isReadonly) return;

      props.onChangeMedia &&
        props.onChangeMedia(
          nodes.filter((x) => isMediaNode(x) && x.mediaId).map((x: ImageElement | VideoElement) => x.mediaId!)
        );
      props.onChange(toHtml(nodes, props.mode));
    },
    [props.onChange, props.isReadonly, props.mode]
  );

  const onBlur = React.useCallback(() => {
    if (!context.hasOpenModal) {
      setIsFocused(false);
      props.onBlur && props.onBlur();
    }
  }, [context.hasOpenModal, props.onBlur]);

  const onFocus = React.useCallback(() => {
    setIsFocused(true);

    props.onFocus && props.onFocus();
  }, [props.onFocus]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        context.toggleFullScreen();
      }
    };

    if (context.isFullScreen) {
      document.addEventListener("keydown", onKeyDown);
    }

    return () => {
      context.isFullScreen && document.removeEventListener("keydown", onKeyDown);
    };
  }, [context.isFullScreen]);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Backspace": {
          // backspace press at the beginning of a tag
          if (
            editor.selection !== null &&
            Range.isCollapsed(editor.selection) &&
            editor.selection.anchor.offset === 0
          ) {
            if (props.mode === "blockBuilder") {
              const selectedChild = editor.children[editor.selection.anchor.path[0]];
              if (isHeadingNode(selectedChild)) {
                e.preventDefault();
                Transforms.setNodes(editor, { type: "p" }, { at: editor.selection?.anchor });
              }
            }

            // redefine default logic immediately deleting medaia nodes by pressing Backspace
            const previousSelectedChild = editor.children[editor.selection.anchor.path[0] - 1];
            if (isMediaNode(previousSelectedChild)) {
              e.preventDefault();
              editor.removeNodes({ at: editor.selection });
              editor.select([editor.selection.anchor.path[0]]);
            }
          }

          break;
        }
        case "Enter": {
          if (props.mode === "singleLinePlain") {
            e.preventDefault();
            return;
          }

          // add new paragraph under media node if it selected and pressed Enter
          if (editor.selection !== null) {
            const selectedChild = editor.children[editor.selection.anchor.path[0]];
            if (isMediaNode(selectedChild)) {
              e.preventDefault();

              editor.insertNodeAt(
                {
                  type: "p",
                  children: [{ text: "" }]
                },
                editor.selection.anchor.path[0] + 1,
                true
              );
            }
          }

          break;
        }
      }
    },
    [editor, props.mode]
  );

  return (
    <div
      className={classNames("rich_editor", props.className, {
        fullscreen: context.isFullScreen,
        inline: props.isInline
      })}
    >
      <Slate key={key} editor={editor} initialValue={value} onValueChange={onValueChange}>
        {!!props.toolbar &&
          (props.isInline ? (
            <HoveringToolbar dropdownClassName={props.dropdownClassName} menu={props.toolbar} />
          ) : (
            <Toolbar dropdownClassName={props.dropdownClassName} isDisabled={props.isDisabled} menu={props.toolbar} />
          ))}

        {!isUndefined(htmlValue) && (
          <textarea ref={textAreaRef} className="content codeview" defaultValue={htmlValue} style={contentStyleBase} />
        )}

        <Content
          availableVideoProviders={props.availableVideoProviders}
          allowVideoFullScreen={props.allowVideoFullScreen}
          autoFocus={props.autoFocus}
          bunnyLibraryId={props.bunnyLibraryId}
          ElementComponent={props.ElementComponent}
          isDisabled={props.isDisabled}
          isReadonly={props.isReadonly}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={props.placeholder}
          style={contentStyle}
        />
      </Slate>
    </div>
  );
};

const RichEditorInner = (props: RichEditorBaseProps) => (
  <RichEditorContextProvider>
    <RichEditorBase {...props} />
  </RichEditorContextProvider>
);

export type RichEditorProps = RichEditorBaseProps & WithValueProps<string>;
const RichEditor = withValue<string, RichEditorBaseProps>(RichEditorInner);
export default RichEditor;
